/* eslint-disable max-lines */
/* eslint-disable complexity */
/* eslint-disable max-lines-per-function */
import { buildLink, checkTimeChange } from '../../../../utils';
import { ArbeitszeitenResult } from '../../hooks/useArbeitszeiten';
import {
  Link,
  Outlet,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import * as z from 'zod';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ErrorPage } from '../../../../app/Routes/AuthenticatedRoutes/ErrorPage';
import { DataTable } from '../../DataTable';
import { useColumns } from '../../columns/columns.tsx';
import { Termine } from './Termine';
import { useFeiertagByStandortIdAndDatum } from '../../../../hooks';
import { DebouncedTextField } from '../../../../components/DebouncedTextField/DebouncedTextField';
import { Abteilung, Arbeitsbereich, Arbeitszeit } from '../../entities';
import {
  AbteilungsFilter,
  ArbeitsbereichFilter,
} from '../../components/Comboboxen';
import { useManagerCanEditArbeitszeiten } from '../../hooks/useManagerCanEditArbeitszeit.ts';
import { faLock, faPlus } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from '@/components/ui/button.tsx';
import { Badge } from '@/components/ui/badge.tsx';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs.tsx';
import { toast } from '@/hooks/use-toast';
import { ToggleFeedback } from '@/components/Feadbacktoggle/index.ts';
import { DienstplanuebernahmeTable } from '../../components/index.ts';
import { ToDoTable } from '../../components/Table/index.ts';
import { useIsMonatAbgeschlossen } from '../../hooks/useIsMonatAbgeschlossen.ts';
import DatePickerWrapper from '../../components/DatePicker/DatePickerWrapper.tsx';
import { useStandortContext } from '@/features/standortContext/StandortContext.tsx';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover.tsx';
import { faSearch } from '@fortawesome/pro-regular-svg-icons';
import { NewDesignBanner } from '@/components/Banner/NewDesignBanner.tsx';

const dateSchema = z.object({
  year: z.string().length(4).regex(/^\d+$/),
  month: z.string().length(2).regex(/^\d+$/),
  day: z.string().length(2).regex(/^\d+$/),
});

export default function Uebersicht({
  arbeitszeiten,
}: {
  arbeitszeiten: ArbeitszeitenResult;
}) {
  const { year, month, day } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const date = useMemo(
    () => new Date(`${year}-${month}-${day}`),
    [year, month, day],
  );
  const umstellung = checkTimeChange(date);

  const { currentStandort } = useStandortContext();
  const { feiertag } = useFeiertagByStandortIdAndDatum({
    standortId: currentStandort.id,
    datum: `${year}-${month}-${day}`,
  });
  const isMonatAbgeschlossen = useIsMonatAbgeschlossen(
    `${year}-${month}-${day}`,
    currentStandort.id,
  );
  const canEditArbeitszeit = useManagerCanEditArbeitszeiten();
  const toDos = arbeitszeiten.todos.toDos;

  const columns = useColumns();

  let defaultActiveTab = 'todo';
  if (toDos.length < 1) {
    defaultActiveTab = 'vollständig';
  }
  const [activeTab, setActiveTab] = useState(defaultActiveTab);
  const [name, setName] = useState('');
  const [arbeitsbereichFilter, setArbeitsbereiche] = useState<Arbeitsbereich[]>(
    [],
  );
  const [abteilungFilter, setAbteilungen] = useState<Abteilung[]>([]);

  useEffect(() => {
    if (searchParams.get('tageswechsel')) {
      toast({
        title: t('domain.arbeitszeiten.create.toast.tageswechsel.title'),
        description: t(
          'domain.arbeitszeiten.create.toast.tageswechsel.description',
          {
            date: `${day}.${month}.${year}`,
          },
        ),
      });
    }
  }, [searchParams, day, month, year, t]);

  useEffect(() => {
    let activeTab = 'todo';
    if (toDos.length < 1) {
      activeTab = 'vollständig';
    }
    setActiveTab(activeTab);
  }, [toDos.length, date]);

  const redirectToDate = useCallback(
    (date: Date) => {
      const currentMonth = date.getMonth() + 1;
      const month = currentMonth.toString().padStart(2, '0');
      const year = date.getFullYear();
      const day = date.getDate().toString().padStart(2, '0');
      navigate(`/arbeitszeiten/uebersicht/${year}/${month}/${day}`);
    },
    [navigate],
  );

  useEffect(() => {
    try {
      dateSchema.parse({
        year: year,
        month: month,
        day: day,
      });
    } catch {
      const currentDate = new Date();
      redirectToDate(currentDate);
    }
  }, [year, month, day, navigate, redirectToDate]);

  if (arbeitszeiten.error) {
    return <ErrorPage error={t('domain.arbeitszeiten.uebersicht.error')} />;
  }

  return (
    <>
      <NewDesignBanner link={buildLink('arbeitszeiten')} />
      <div className="pt-8 bg-white">
        <ToggleFeedback />
        <div className="w-full flex justify-between items-start px-8 pb-8">
          <div className="flex flex-col items-start gap-3 sm:flex-row sm:items-center">
            <DatePickerWrapper
              date={date}
              onSelect={(d) => {
                redirectToDate(d);
              }}
              onBackward={(d) => {
                redirectToDate(d);
              }}
              onForward={(d) => {
                redirectToDate(d);
              }}
            />
            {umstellung && (
              <Badge className="cursor-default">
                {t(`domain.arbeitszeiten.uebersicht.zeitUmstellung`, {
                  zeit: umstellung,
                })}
              </Badge>
            )}
            <div className="flex gap-3 items-center">
              <Termine />
              {feiertag && (
                <Badge className="cursor-default bg-secondary-purple-P500 hover:bg-secondary-purple-P500 whitespace-nowrap">
                  {feiertag}
                </Badge>
              )}
            </div>
          </div>
          <div className="flex justify-end gap-3 items-center">
            {canEditArbeitszeit && (
              <Link
                to={`/arbeitszeiten/uebersicht/${year}/${month}/${day}/sheet/create`}>
                <Button
                  variant="default"
                  data-testid="create-arbeitszeit"
                  size="icon"
                  disabled={isMonatAbgeschlossen}>
                  <FontAwesomeIcon
                    icon={isMonatAbgeschlossen ? faLock : faPlus}
                  />
                </Button>
              </Link>
            )}
          </div>
        </div>
        <Tabs
          defaultValue="vollständig"
          onValueChange={(value) => setActiveTab(value)}
          value={activeTab}
          className="flex flex-col">
          <div className="flex flex-row justify-between px-8 gap-3">
            <TabsList className="flex justify-start gap-4 w-fit overflow-x-scroll h-auto">
              <TabsTrigger
                className="flex items-center gap-2"
                key={'vollständig'}
                value="vollständig">
                {t('domain.arbeitszeiten.uebersicht.tabellenTabs.vollstaendig')}
                <Badge className="rounded-full h-auto w-auto px-1.5 flex items-center justify-center font-bold text-xs">
                  {arbeitszeiten.abgeschlosseneArbeitszeiten.length}
                </Badge>
              </TabsTrigger>
              <TabsTrigger
                className="flex items-center gap-2"
                key={'todo'}
                value="todo">
                {t('domain.arbeitszeiten.uebersicht.tabellenTabs.todo')}
                <Badge
                  className={`rounded-full h-auto w-auto px-1.5 flex items-center justify-center font-bold text-xs ${
                    toDos.length > 0
                      ? 'bg-primary-red-R500 hover:bg-primary-red-R400'
                      : ''
                  }`}>
                  {toDos.length}
                </Badge>
              </TabsTrigger>
              {canEditArbeitszeit && (
                <TabsTrigger
                  className="flex items-center gap-2"
                  key={'dienstplanuebernahme'}
                  value="dienstplanuebernahme">
                  {t(
                    'domain.arbeitszeiten.uebersicht.tabellenTabs.dienstplanuebernahmen',
                  )}
                  <Badge className="rounded-full h-auto w-auto px-1.5 flex items-center justify-center font-bold text-xs">
                    {arbeitszeiten.dienstplanuebernahmen.length}
                  </Badge>
                </TabsTrigger>
              )}
            </TabsList>
            <div className="flex gap-3 items-center lg:hidden">
              <Popover>
                <PopoverTrigger>
                  <div className="relative">
                    {name.length > 0 && (
                      <span className="absolute top-[5px] right-[5px] size-[5px] inline-block text-center font-semibold rounded-full bg-primary-blue-B600" />
                    )}
                    <Button variant="ghost" size="icon">
                      <FontAwesomeIcon icon={faSearch} />
                    </Button>
                  </div>
                </PopoverTrigger>
                <PopoverContent className="bg-white w-full">
                  <DebouncedTextField
                    placeholder="Namen suchen"
                    value={name}
                    onChange={(event) => {
                      if (typeof event === 'string') {
                        setName(event);
                        return;
                      }
                      setName(event.toString());
                    }}
                    className="w-[280px]"
                  />
                </PopoverContent>
              </Popover>
              {currentStandort.abteilungenEnabled ? (
                <AbteilungsFilter
                  onSelect={(abteilung) => {
                    setAbteilungen(abteilung);
                  }}
                />
              ) : (
                <ArbeitsbereichFilter
                  onSelect={(arbeitsbereich) => {
                    setArbeitsbereiche(arbeitsbereich);
                  }}
                />
              )}
            </div>
            <div className="hidden lg:flex lg:gap-3 lg:items-center">
              <DebouncedTextField
                placeholder={t(
                  'domain.arbeitszeiten.uebersicht.suche.placeholder',
                )}
                className="w-[280px]"
                value={name}
                onChange={(event) => {
                  if (typeof event === 'string') {
                    setName(event);
                    return;
                  }
                  setName(event.toString());
                }}
              />
              {currentStandort.abteilungenEnabled ? (
                <AbteilungsFilter
                  onSelect={(abteilung) => {
                    setAbteilungen(abteilung);
                  }}
                />
              ) : (
                <ArbeitsbereichFilter
                  onSelect={(arbeitsbereich) => {
                    setArbeitsbereiche(arbeitsbereich);
                  }}
                />
              )}
            </div>
          </div>

          <TabsContent
            key={'vollständig'}
            value="vollständig"
            className="bg-neutrals-N200 md:px-8 px-4 overflow-x-scroll">
            <DataTable
              columns={columns.abgeschlosseneArbeitszeiten as Arbeitszeit[]}
              data={arbeitszeiten.abgeschlosseneArbeitszeiten}
              nameFilter={name}
              arbeitsbereichFilter={arbeitsbereichFilter}
              abteilungFilter={abteilungFilter}
            />
          </TabsContent>
          <TabsContent
            key={'todo'}
            value="todo"
            className="bg-neutrals-N200 md:px-8 px-4 overflow-x-scroll">
            <ToDoTable
              mitarbeiterFilter={name}
              arbeitsbereichFilter={arbeitsbereichFilter}
              abteilungFilter={abteilungFilter}
              data={arbeitszeiten.todos.toDos}
            />
          </TabsContent>
          {canEditArbeitszeit && (
            <TabsContent
              key={'dienstplanuebernahme'}
              value="dienstplanuebernahme"
              className="bg-neutrals-N200 md:px-8 px-4 overflow-x-scroll">
              <DienstplanuebernahmeTable
                data={arbeitszeiten.dienstplanuebernahmen}
                nameFilter={name}
                arbeitsbereichFilter={arbeitsbereichFilter}
                abteilungFilter={abteilungFilter}
              />
            </TabsContent>
          )}
        </Tabs>
        <Outlet />
      </div>
    </>
  );
}
